/**
 * Workspace failure & recovery copy — Ori, 19 Jul 2026. SHIP VERBATIM.
 *
 * Register: amber, not red. No "Oops", no exclamation marks, no apology
 * stacking. `ScanLoader`'s playful tone is a SUCCESS-path voice and is
 * deliberately not carried here.
 *
 * Each entry is { d } or { d, m } — desktop and (where it differs) mobile.
 * Mobile strings that Ori wrote as two lines are arrays; render one per line.
 * Do NOT paraphrase for space: the mobile variants ARE the paraphrase, already
 * written, already checked at 320px.
 *
 * HONESTY CONDITIONS (spec §"Honesty conditions" — build requirements):
 *   1. `down.watching` may only render while a poll is genuinely running.
 *      When the poll caps out, render `down.watchingPaused` instead — hiding
 *      the line is NOT sufficient, because the user's last information would
 *      remain "they're watching for me". Driven by watchStatus.
 *   2. `retry.attempt` says "now" — it may only render while a request is
 *      actually open, never while one is merely scheduled.
 *   3. `offline.body` promises auto-reconnect and requires the `online`
 *      listener to exist.
 */

export const WS_COPY = {
  retry: {
    title: { d: "Trouble loading Leadmato" },
    // {n} = attempt number. Renders ONLY while a request is genuinely open.
    attempt: {
      d: (n) => `Trying again now — attempt ${n} of 5`,
      m: (n) => `Trying again now — ${n} of 5`,
    },
    // Deliberately does not start with "Trying again": under reduced motion the
    // text swap is the ONLY signal that state changed, so a different first
    // word is what makes the change readable.
    countdown: { d: (n) => `Next try in ${n}s` },
  },
  down: {
    title: { d: "Still can't load Leadmato" },
    body: {
      d: "Your plan and your leads are fine — nothing on your account has changed. We just can't reach them from here right now.",
      m: ["Your plan and your leads are fine.", "We just can't reach them right now."],
    },
    watching: {
      d: "We're still checking in the background.",
      m: "Still checking in the background.",
    },
    watchingPaused: {
      d: "We've stopped checking for now. Try again whenever you're ready.",
      m: "We've stopped checking for now.",
    },
    cta: { d: "Try again" },
    lastresort: {
      d: "Still nothing. Refreshing the page is worth a try.",
      m: "Still nothing. Try refreshing the page.",
    },
  },
  healed: {
    d: "Back online — everything's working again.",
    m: "Back online. Everything's working again.",
  },
  offline: {
    title: { d: "You're offline" },
    body: {
      d: "Check your wifi or mobile data. We'll reconnect on our own the moment you're back.",
      m: ["Check your wifi or mobile data.", "We'll reconnect on our own."],
    },
    cta: { d: "Try again" },
  },
  gate: {
    // Existing "Checking…" — unchanged, still true while a fetch is in flight.
    pending: { d: "Checking…" },
    // "Paused", not "Unavailable": a paying owner reads "Unavailable" next to a
    // locked feature as "not included in your plan" — an upsell by implication,
    // which §6 forbids. "Paused" implies no tier, no permission, no promise.
    unknown: { d: "Paused" },
    unknownAria: { d: "Paused. See the message at the top of the page." },
  },
};

/** Pick the desktop or mobile variant. `v` is a copy node; `n` is any arg. */
export function pick(node, isMobile, n) {
  const raw = isMobile && node.m !== undefined ? node.m : node.d;
  return typeof raw === "function" ? raw(n) : raw;
}
